import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useGetCmsEntry, useCreateCms, useUpdateCms } from '@/hooks/useCms';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import RichTextEditor from '@/components/cms/RichTextEditor';

const schema = z.object({
  title: z.string().min(1, 'Title is required').max(255),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(100)
    .regex(/^[a-z0-9\-]+$/, 'Only lowercase letters, numbers and hyphens allowed'),
  meta_title: z.string().max(255).optional().or(z.literal('')),
  meta_description: z.string().max(500).optional().or(z.literal('')),
  content: z.string().min(1, 'Content is required'),
});

type FormValues = z.infer<typeof schema>;

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s\-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function CmsFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const { data: existingData, isLoading: isLoadingEntry } = useGetCmsEntry(id ?? '');
  const createMutation = useCreateCms();
  const updateMutation = useUpdateCms(id ?? '');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      slug: '',
      meta_title: '',
      meta_description: '',
      content: '',
    },
  });

  const titleValue = watch('title');

  // Auto-generate slug from title (only when not manually edited)
  useEffect(() => {
    if (!isEdit && !slugManuallyEdited && titleValue) {
      setValue('slug', slugify(titleValue), { shouldValidate: false });
    }
  }, [titleValue, isEdit, slugManuallyEdited, setValue]);

  // Pre-populate form in edit mode
  useEffect(() => {
    if (isEdit && existingData?.data) {
      const entry = existingData.data;
      reset({
        title: entry.title ?? '',
        slug: entry.slug ?? '',
        meta_title: entry.meta_title ?? '',
        meta_description: entry.meta_description ?? '',
        content: entry.content ?? '',
      });
      setSlugManuallyEdited(true);
    }
  }, [isEdit, existingData, reset]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  const onSubmit = async (values: FormValues) => {
    const payload = {
      title: values.title,
      slug: values.slug,
      content: values.content,
      meta_title: values.meta_title || undefined,
      meta_description: values.meta_description || undefined,
    };

    if (isEdit) {
      updateMutation.mutate(payload, {
        onSuccess: () => {
          toast.success('CMS page updated successfully');
          navigate('/cms');
        },
        onError: (err: Error) => toast.error(err.message),
      });
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.success('CMS page created successfully');
          navigate('/cms');
        },
        onError: (err: Error) => toast.error(err.message),
      });
    }
  };

  if (isEdit && isLoadingEntry) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold">{isEdit ? 'Edit CMS Page' : 'Add CMS Page'}</h1>
        <p className="text-sm text-muted-foreground">
          {isEdit ? 'Update the content for this page.' : 'Create a new dynamic content page.'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Title */}
        <div className="space-y-1.5">
          <Label>
            Title <span className="text-destructive">*</span>
          </Label>
          <Input
            placeholder="About Us"
            {...register('title')}
          />
          {errors.title && (
            <p className="text-xs text-destructive">{errors.title.message}</p>
          )}
        </div>

        {/* Slug */}
        <div className="space-y-1.5">
          <Label>
            Slug <span className="text-destructive">*</span>{' '}
            <span className="text-muted-foreground text-xs">(auto-generated from title, but editable)</span>
          </Label>
          <Input
            placeholder="about-us"
            {...register('slug', {
              onChange: () => {
                setSlugManuallyEdited(true);
              },
            })}
          />
          {errors.slug && (
            <p className="text-xs text-destructive">{errors.slug.message}</p>
          )}
        </div>

        {/* Meta Title */}
        <div className="space-y-1.5">
          <Label>
            Meta Title <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          <Input
            placeholder="About LeadNova — B2B Lead Management"
            {...register('meta_title')}
          />
          {errors.meta_title && (
            <p className="text-xs text-destructive">{errors.meta_title.message}</p>
          )}
        </div>

        {/* Meta Description */}
        <div className="space-y-1.5">
          <Label>
            Meta Description <span className="text-muted-foreground text-xs">(optional, max 500 chars)</span>
          </Label>
          <textarea
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
            placeholder="A short description that appears in search engine results…"
            maxLength={500}
            {...register('meta_description')}
          />
          {errors.meta_description && (
            <p className="text-xs text-destructive">{errors.meta_description.message}</p>
          )}
        </div>

        {/* Content — TinyMCE */}
        <div className="space-y-1.5">
          <Label>
            Content <span className="text-destructive">*</span>
          </Label>
          <Controller
            name="content"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                value={field.value}
                onChange={field.onChange}
                siteContentId={id}
                height={500}
              />
            )}
          />
          {errors.content && (
            <p className="text-xs text-destructive">{errors.content.message}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/cms')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
